import {
  AppBar,
  Avatar,
  Breadcrumbs,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Popover,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  capitalize,
  kebabToLabel,
  slugToLabel,
} from '../../utilities/stringUtils'

import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import Credits from '../Credits'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import SidePanel from '../SidePanel'
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'
import { useState } from 'react'
import { useUser } from '../../context/CurrentUserContext'

const isId = (s) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s) ||
  (s.length >= 20 && /^[a-zA-Z0-9]+$/.test(s))

const TopBar = ({ onMenuToggle, isCollapsed, sidebarProps }) => {
  const { user, logout } = useUser()
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const { PageTitle, breadcrumbs } = usePageTitle()
  const [isProfileCardOpen, setIsProfileCardOpen] = useState(false)
  const [profileCardAnchor, setProfileCardAnchor] = useState(null)
  const [selectedPanel, setSelectedPanel] = useState(null)

  const handleLogout = async () => {
    try {
      await fetchWithAuth('logout', { method: 'POST' })
      logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed', error)
    }
    setProfileCardAnchor(null)
  }

  const panelMeta = {
    credits: { title: 'Credits', content: <Credits /> },
  }

  const profileMenuOptions = [
    {
      label: 'Credits',
      icon: <ArticleOutlinedIcon />,
      action: () => {
        setIsProfileCardOpen(false)
        setSelectedPanel('credits')
      },
    },
    {
      label: 'Support',
      icon: <SupportOutlinedIcon />,
      action: () => {
        window.location.href = 'mailto:techsupport@holloid.com'
      },
    },
    {
      label: 'Logout',
      icon: <LogoutOutlinedIcon />,
      action: handleLogout,
    },
  ]

  const createBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter((x) => x)
    const usingFallback = !breadcrumbs.length
    const breadcrumbList = usingFallback ? paths : breadcrumbs
    return breadcrumbList.map((path, index) => {
      const routeTo = `/${paths.slice(0, index + 1).join('/')}`
      const isLast = index === breadcrumbList.length - 1
      if (usingFallback && isId(path)) {
        return (
          <Skeleton
            key={path}
            variant="text"
            width={80}
            sx={{ display: 'inline-block' }}
          />
        )
      }
      const displayText =
        usingFallback && isLast && PageTitle
          ? capitalize(PageTitle)
          : kebabToLabel(path)
      return isLast ? (
        <Typography key={path} color="inherit">
          {displayText}
        </Typography>
      ) : (
        <MuiLink
          component={Link}
          to={routeTo}
          key={path}
          color="inherit"
          className="capitalize"
        >
          {displayText}
        </MuiLink>
      )
    })
  }

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: `calc(100% - ${isCollapsed ? sidebarProps.collapsedWidth : sidebarProps.width}px)`,
          },
          ml: {
            sm: `${isCollapsed ? sidebarProps.collapsedWidth : sidebarProps.width}px`,
          },
          backgroundColor: theme.palette.menu.background.default,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
        elevation={0}
      >
        <Toolbar elevation={0}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={onMenuToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              color: theme.palette.menu.text.default,
            }}
          >
            <MenuIcon />
          </IconButton>

          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ flexGrow: 1 }}
            color={theme.palette.menu.text.default}
          >
            {createBreadcrumbs()}
          </Breadcrumbs>

          {/* Header options */}
          <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
            <IconButton
              sx={{
                color: theme.palette.menu.text.default,
                '&.Mui-focusVisible': {
                  outline: `2px solid ${theme.palette.menu.text.default}`,
                },
              }}
              onClick={() => navigate('/users/me')}
              aria-label="settings"
            >
              <SettingsOutlinedIcon></SettingsOutlinedIcon>
            </IconButton>
            <IconButton
              sx={{
                color: theme.palette.menu.text.default,
                '&.Mui-focusVisible': {
                  outline: `2px solid ${theme.palette.menu.text.default}`,
                },
              }}
              aria-label="account"
              onClick={(e) => {
                setIsProfileCardOpen(true)
                setProfileCardAnchor(e.target)
              }}
            >
              <AccountCircleIcon></AccountCircleIcon>
            </IconButton>
          </Stack>

          {/* Profile card */}
          {user && (
            <Popover
              anchorEl={profileCardAnchor}
              open={isProfileCardOpen}
              onClose={() => setIsProfileCardOpen(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { 'aria-label': 'Profile menu' } }}
              disableEnforceFocus
            >
              <Stack
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsProfileCardOpen(false)
                  }
                }}
              >
                <ListItem>
                  <Stack gap={2}>
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Typography color="primary.contrastText">
                          {user.profile?.firstName.charAt(0).toUpperCase()}
                          {user.profile?.lastName.charAt(0).toUpperCase()}
                        </Typography>
                      </Avatar>
                      <Stack>
                        <Typography variant="body1">
                          {capitalize(user.profile?.firstName)}{' '}
                          {capitalize(user.profile?.lastName)}
                        </Typography>
                        <Typography color="secondary" variant="body2">
                          {user.email}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Typography
                      variant="body2"
                      width={'100%'}
                      color="secondary"
                      align="center"
                      py={1}
                    >
                      {slugToLabel(user.role)} at {user.organization.name}
                    </Typography>
                  </Stack>
                </ListItem>
                <Divider />
                <List disablePadding>
                  {profileMenuOptions.map((option) => (
                    <ListItem key={option.label} disablePadding>
                      <ListItemButton
                        aria-label={option.label}
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setIsProfileCardOpen(false)
                          option.action()
                        }}
                      >
                        <ListItemIcon>{option.icon}</ListItemIcon>
                        <ListItemText primary={option.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Divider />
                <ListItem>
                  <Typography
                    variant="body2"
                    color="secondary"
                    align="center"
                    width={'100%'}
                  >
                    {`Hollo Portal ${
                      import.meta.env.VITE_APP_ENVIRONMENT !== 'production'
                        ? import.meta.env.VITE_APP_ENVIRONMENT
                        : ''
                    }`}
                  </Typography>
                </ListItem>
              </Stack>
            </Popover>
          )}
        </Toolbar>
      </AppBar>
      <SidePanel
        title={panelMeta[selectedPanel]?.title}
        open={selectedPanel !== null}
        onClose={() => setSelectedPanel(null)}
        overlay
      >
        {panelMeta[selectedPanel]?.content}
      </SidePanel>
    </>
  )
}

export default TopBar
